import {Empty} from 'antd';
import cn from 'classnames';
import React, {
    Dispatch,
    forwardRef,
    SetStateAction,
    useImperativeHandle,
    useState,
} from 'react';

import {customTableUtils} from 'components/@common/widgets/custom-table';
import {useCustomTable, IUseCustomTableArgs} from 'components/@common/widgets/custom-table/custom-table';
import {IColumnSortData} from 'components/@common/widgets/custom-table/table-column-menu';
import {Spinner} from 'components/spinner';
import {useAfterEffect} from 'shared/hooks/use-after-effect';
import {usePagination} from 'shared/hooks/use-pagination';
import {useUserRoleFunctions} from 'shared/hooks/use-user-role-functions';
import {TStateSetter} from 'shared/types/generics';
import {getTablePaginationDefaultConfig} from 'shared/utils';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {externalLoadingSelectors} from 'store/slices/external-loading/external-loading';
import {loadOpenInterface} from 'store/slices/external-loading/external-loading-thunks';
import {selectIsThunkPending} from 'store/slices/loading-state-slice';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {
    OPEN_INTERFACE_ROLE_SECTION, DEFAULT_PAGE_SIZE,
} from '../../model/constants/open-interface-constants';
import {IOpenInterfaceParametersFilterForm} from '../../model/types/open-interface-types';
import styles from './open-interface-table.module.less';

const {
    setHeaderStyles,
} = customTableUtils;

interface IOpenInterfaceTableProps {
    openInterfaceParametersFilter?: IOpenInterfaceParametersFilterForm;
    isNeedFetchTableData?: boolean;
    setIsNeedFetchTableData: Dispatch<SetStateAction<boolean>>;
}
export interface IOpenInterfaceTableRef {
    setSortData: TStateSetter<IColumnSortData>;
}

export const OpenInterfaceTable = forwardRef<
IOpenInterfaceTableRef, IOpenInterfaceTableProps>((
    {
        openInterfaceParametersFilter,
        isNeedFetchTableData,
        setIsNeedFetchTableData,
    }: IOpenInterfaceTableProps,
    ref,
) => {
    const dispatch = useAppDispatch();
    const [sortData, setSortData] = useState<IColumnSortData>({
        sort: {},
    });

    const isLoadingOpenInterfaceData = useAppSelector(s => selectIsThunkPending(
        s, loadOpenInterface.typePrefix,
    ));

    const documents = useAppSelector(externalLoadingSelectors.selectOpenInterfaceData);
    const columns: IUseCustomTableArgs['columns'] = useAppSelector(externalLoadingSelectors.selectOpenInterfaceColumns);
    const openInterfaceTotalCount = documents?.length ?? 0;

    const shouldLoadWithoutFilters = false;

    const {hasUserRoleFunction, isLoadingUserFunctions} = useUserRoleFunctions({
        sections: [OPEN_INTERFACE_ROLE_SECTION],
    });

    const {
        current, pageSize, handleChange, setCurrentManually,
    } = usePagination({
        pageDefault: 1,
        pageSizeDefault: DEFAULT_PAGE_SIZE,
    });

    useImperativeHandle(ref, () => ({
        setSortData,
    }));

    const fetchTableData = ({scenarioId, loadId, srcId}: {
        scenarioId: number | undefined; loadId: number | undefined; srcId?: number;
    }) => {
        const isScenarioIdValid = Number.isFinite(scenarioId);
        const isLoadIdValid = Number.isFinite(loadId);

        if (isScenarioIdValid && isLoadIdValid) {
            return dispatch(loadOpenInterface({
                scenarioId: scenarioId!,
                loadId: loadId!,
                srcId,
            }));
        }

        return undefined;
    };

    const {
        customTableJSX,
    } = useCustomTable({
        tableColumnSortData: sortData,
        setTableColumnSortData: setSortData,
        pagination: {
            ...getTablePaginationDefaultConfig({
                pageSizeOptions: ['5', '10', '20', '50', '100', '500'],
            }),
            current,
            total: (() => {
                if (!isLoadingUserFunctions && !hasUserRoleFunction(EUserRoleFunction.VIEW)) return undefined;
                return openInterfaceTotalCount;
            })(),
            pageSize,
            onChange: handleChange,
        },
        rowKey: 'id',
        className: cn(styles['open-interface-table'], 'mt-5'),
        locale: {
            emptyText: (
                <div style={{marginTop: 30}}>
                    {(() => {
                        if (!hasUserRoleFunction(EUserRoleFunction.VIEW) && isLoadingUserFunctions) {
                            return <div><Spinner tip="Загрузка..." /></div>;
                        }
                        if (!hasUserRoleFunction(EUserRoleFunction.VIEW) && !isLoadingUserFunctions) {
                            return 'Недостаточно прав для просмотра данных';
                        }
                        if ((openInterfaceParametersFilter
                            || shouldLoadWithoutFilters) && !isLoadingOpenInterfaceData) {
                            return <Empty description="Нет данных" />;
                        }
                        return 'Для формирования данных необходимо задать параметры';
                    })()}
                </div>
            ),
        },
        dataSource: (() => {
            if (isLoadingUserFunctions) return [];
            if (!isLoadingUserFunctions && !hasUserRoleFunction(EUserRoleFunction.VIEW)) return [];
            if (!openInterfaceParametersFilter && !shouldLoadWithoutFilters) return [];
            return documents;
        })(),
        onColumn: col => ({
            ...setHeaderStyles({
                minWidth: 'fit-content',
                flexGrow: 1,
                textAlign: 'center',
                fontSize: '16px',
                padding: '8px 8px 8px 8px',
            }),
            render: col.render || ((value: any) => (
                <div className="text-center">
                    {value}
                </div>
            )),
            ...col,
        }),
        bordered: true,
        columns,
    });

    useAfterEffect(() => {
        const loadThunk = fetchTableData({
            scenarioId: openInterfaceParametersFilter?.scenarioId,
            loadId: openInterfaceParametersFilter?.loadId,
            srcId: openInterfaceParametersFilter?.srcId,
        });

        setCurrentManually(1);

        loadThunk?.finally(() => {
            setIsNeedFetchTableData(false);
        });

        return () => {
            loadThunk?.abort();
        };
    }, [openInterfaceParametersFilter, isNeedFetchTableData]);

    return <>{customTableJSX}</>;
});
